import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import purify from 'dompurify';
import { Grid, Text } from 'elements';
import { RootState } from 'modules';
import Reply from 'components/base/reply/';
import { showAlert, showConfirm } from 'components/modal/showConfirm';
import {
  getQNAItem,
  delQNAItem,
  getQNAReply,
  createQNAReply,
  updateQNAReply,
  delQNAReply,
  likeQNAReply,
} from 'libs/api/case';
import { toUpperCase } from 'libs/utils';
import { TQNAItem } from 'modules/case/types';
import { CWindowWidth } from 'constants/base.const';
import { CMentoringFieldType } from 'constants/mentoring.const';
import { EUserType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Button, Spin, Tabs } from 'antd';
import styled from 'styled-components';
import { Colors, SecondaryColor } from 'GlobalStyles';
import { device } from 'GlobalStyles';
import DefaultUser from 'assets/icons/default.webp';
import MainBackImage from 'assets/images/banners/4.webp';

const { TabPane } = Tabs;

const MentoringQNADetail = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const { id }: { id: string } = useParams();
  const history = useHistory();
  const [QNA, setQNA] = useState<TQNAItem>();
  const getQNA = async () => {
    const { success, data } = await getQNAItem(id);
    if (success) {
      setQNA(data);
    }
  };

  const delQNA = () => {
    showConfirm(
      async () => {
        const { success } = await delQNAItem(id);
        if (success) history.push(`${ERoute.MentoringQNA}`);
      },
      '해설 삭제',
      '해설을 삭제하시겠습니까?',
    );
  };

  useEffect(() => {
    getQNA();
  }, []);

  // 디테일페이지에서 상단메뉴의 다른 필드를 선택하면 그 필드의 리스트 페이지로 이동할 수 있도록
  const fieldError = () => {
    if (QNA && currentUser) {
      if (currentUser?.primary_field !== QNA?.mentoring_field) {
        //다른 필드 누르면 알림창뜨고 그 필드의 리스트 페이지로 이동
        history.goBack();
        if (QNA) {
          showAlert(
            undefined,
            '',
            <div>
              <p>해당 글은 {QNA?.mentoring_field && toUpperCase(QNA.mentoring_field)}분야의 Q&A입니다.</p>
              <p>
                {currentUser?.primary_field && toUpperCase(currentUser.primary_field)}
                분야의 Q&A로 이동합니다.
              </p>
            </div>,
          );
        }
      }
    }
  };

  useEffect(() => {
    fieldError();
  }, [currentUser, QNA]);

  return (
    <>
      {QNA?.mentoring_field && (
        <Helmet>
          <title>{CMentoringFieldType[QNA.mentoring_field]} - Q&A</title>
        </Helmet>
      )}
      <Grid image={MainBackImage} imageSize='cover' height='300px' md={{ height: '250px' }}>
        <Grid height='100%' backgroundColor={`${Colors.black}33`} justify='center'>
          <Grid width={`${CWindowWidth}px`} padding='0 7%' align='center' md={{ padding: '0 2%' }}>
            <Grid>
              <Text color={Colors.white} size='50px' bold>
                Q&A
              </Text>
            </Grid>
            {currentUser?.primary_field === 'consulting' ? (
              <Grid marginTop='5px'>
                <Text color={Colors.white} size='16px' md={{ size: '12px' }}>
                  컨설팅 취업준비와 관련된 무엇이든 물어보세요. Career Step 멘토가 답해드립니다.
                </Text>
              </Grid>
            ) : (
              <Grid marginTop='5px'>
                <Text color={Colors.white} size='16px' md={{ size: '12px' }}>
                  취업준비와 관련된 무엇이든 물어보세요. Career Step 멘토가 답해드립니다.
                </Text>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Tabs centered={true}>
        {currentUser?.primary_field && (
          <TabPane tab={`${toUpperCase(currentUser?.primary_field)} Q&A`} key={ERoute.MentoringQNA}></TabPane>
        )}
      </Tabs>

      <Grid justify='center'>
        <Grid width={`${CWindowWidth}px`} padding='38px 20px 150px 20px'>
          {!QNA ? (
            <Grid justify='center' align='center' height='100px'>
              <Spin spinning />
            </Grid>
          ) : (
            <>
              <Grid isNoWrap justify='space-between' align='flex-end'>
                <Grid isInline>
                  <Grid gap='10px' align='center'>
                    <Text bold>작성일자</Text>
                    <Text>{QNA.created_at || '-'}</Text>
                  </Grid>
                  <Grid marginTop='20px'>
                    {QNA.categories?.map((category: { id: number; name: string }) => (
                      <Button
                        className='small'
                        style={{
                          minHeight: '22px',
                          marginRight: '5px',
                          fontSize: '1rem',
                        }}
                        key={category.id}
                      >
                        {' '}
                        {category.name}{' '}
                      </Button>
                    ))}
                  </Grid>
                  <Grid marginTop='20px'>
                    {QNA.sub_categories?.map((category: { id: number; name: string }) => (
                      <Button
                        className='small'
                        style={{
                          minHeight: '22px',
                          marginRight: '5px',
                          fontSize: '1rem',
                        }}
                        key={category.id}
                      >
                        {' '}
                        {category.name}{' '}
                      </Button>
                    ))}
                  </Grid>
                </Grid>
                <Button type='primary' onClick={() => history.go(-1)}>
                  목록
                </Button>
              </Grid>
              <Grid
                position='relative'
                border={`1px solid ${Colors.grey5}`}
                minHeight='300px'
                align='flex-start'
                marginTop='35px'
              >
                <StyledTitle>
                  <Text style={{ flex: '1' }}>{QNA.title || '-'}</Text>

                  {/* 작성자 */}
                  {QNA.created_by && currentUser?.user_type === 'mentor' && (
                    <div style={{ flexBasis: 'auto' }}>
                      <Grid
                        gap='8px'
                        align='center'
                        onClick={() => history.push(`/mentoring/mentee_view/${QNA.mentee_id}`)}
                      >
                        <Grid
                          isInline
                          image={QNA.created_by.profile_image?.absolute_url || DefaultUser}
                          width='30px'
                          height='30px'
                          borderRadius='40px'
                        />
                        <Text>{QNA.created_by.fullname}</Text>
                        <Grid isInline padding='2px 8px' backgroundColor={SecondaryColor} borderRadius='4px'>
                          <Text color={Colors.white} size='0.9rem'>
                            {QNA.user_type === EUserType.Mentee ? '멘티' : '멘토'}
                          </Text>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </StyledTitle>
                <Grid padding='14px 20px'>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: purify.sanitize(QNA.body || '-'),
                    }}
                  />
                </Grid>
              </Grid>
              {!!currentUser && currentUser.id === QNA.created_by?.id && (
                <Grid marginTop='20px' justify='flex-end' align='center' gap='20px'>
                  <Button type='primary' onClick={delQNA}>
                    삭제
                  </Button>
                  <Button type='primary' onClick={() => history.push(`${ERoute.MentoringQNAWrite}?id=${id}`)}>
                    수정
                  </Button>
                </Grid>
              )}
            </>
          )}

          <Grid marginTop='50px' style={{ borderTop: `1px solid ${Colors.grey1}` }} />

          <Grid marginTop='50px'>
            <Reply
              page_type='caseQNA'
              id={Number(id)}
              getReply={getQNAReply}
              onSave={createQNAReply}
              onEdit={updateQNAReply}
              //좋아요는 구현 X
              like={likeQNAReply}
              del={delQNAReply}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default MentoringQNADetail;

const StyledTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  width: 100%;
  border-bottom: 1px solid ${Colors.grey5};
  @media screen and ${device.xs} {
    flex-direction: column;
    align-items: start;
  }
`;
